import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Fab,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Close, GpsFixed, Search } from '@mui/icons-material';
import { GameStatus } from '../../constants/gameConstants';
import { filterFindMeEntries } from '../../helpers/findMe';

const statusChip = (entry) => {
  if (entry.playerStatus && entry.playerStatus !== 'Active') {
    return { label: entry.playerStatus, color: 'warning' };
  }

  switch (entry.currentStatus) {
    case GameStatus.IN_PROGRESS:
      return { label: entry.statusLabel, color: 'primary' };
    case GameStatus.COMPLETE:
      return { label: entry.statusLabel, color: 'success' };
    case GameStatus.PLAYOFF:
      return { label: entry.statusLabel, color: 'warning' };
    case GameStatus.NOT_STARTED:
      return { label: entry.statusLabel, color: 'default' };
    default:
      return null;
  }
};

const getDetailLines = (located) => {
  if (!located) {
    return [];
  }

  const lines = [];
  const location = [located.sectionName, located.venue].filter(Boolean).join(' · ');
  if (location) {
    lines.push(location);
  }

  if (located.sectionId) {
    const game = [
      located.rink,
      located.currentRound ? `Round ${located.currentRound}` : '',
      located.statusLabel
    ].filter(Boolean);

    lines.push(game.length ? game.join(' · ') : 'No game information yet.');

    if (located.nextGame) {
      lines.push(`Next game: ${located.nextGame}`);
    }
  } else {
    lines.push(
      located.inLog
        ? 'Your team is listed in the log below.'
        : 'Your team is not in the current log yet.'
    );
  }

  if (located.playerStatus && located.playerStatus !== 'Active') {
    lines.push(`Status: ${located.playerStatus}`);
  }

  return lines;
};

const isBsaQuery = (query) =>
  /^\s*(?:bsa\s*[:#.-]?\s*)?\d[\d\s.-]*$/i.test(query);

const EmbeddedFindMe = ({ entries = [], located, onLocate, onClear }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [query, setQuery] = useState('');
  // The row cannot be scrolled to until the dialog has released the body
  // scroll lock, so hand the selection to the parent after the exit finishes.
  const pendingEntryRef = useRef(null);

  const { results, total, truncated } = useMemo(
    () => filterFindMeEntries(entries, query),
    [entries, query]
  );

  const hasQuery = Boolean(query.trim());
  const detailLines = getDetailLines(located);

  useEffect(
    () => () => {
      pendingEntryRef.current = null;
    },
    []
  );

  const handleOpen = () => {
    setQuery('');
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  const handleSelect = (entry) => {
    pendingEntryRef.current = entry;
    setDialogOpen(false);
  };

  const handleExited = () => {
    const entry = pendingEntryRef.current;
    pendingEntryRef.current = null;

    // Reset after the exit so the results do not vanish while the dialog fades.
    setQuery('');

    if (entry) {
      onLocate(entry);
    }
  };

  if (!entries.length && !located) {
    return null;
  }

  const noMatchMessage = isBsaQuery(query)
    ? 'No player with that BSA number is listed in this event.'
    : 'No player with that name is listed in this event. Try a surname only.';

  return (
    <>
      {located && (
        <Alert
          severity={located.playerStatus !== 'Active' ? 'warning' : 'info'}
          icon={<GpsFixed fontSize="inherit" />}
          onClose={onClear}
          slotProps={{ closeButton: { 'aria-label': 'Clear find me location' } }}
          sx={{
            position: 'sticky',
            top: 8,
            zIndex: (activeTheme) => activeTheme.zIndex.appBar,
            mb: 2,
            textAlign: 'left'
          }}>
          <AlertTitle sx={{ mb: 0.5 }}>You are here: {located.playerName}</AlertTitle>
          {located.teamName && (
            <Typography variant="body2" fontWeight="bold">
              {located.teamName}
            </Typography>
          )}
          {detailLines.map((line, index) => (
            <Typography variant="body2" key={`${index}-${line}`}>
              {line}
            </Typography>
          ))}
        </Alert>
      )}

      {entries.length > 0 && (
        <Fab
          variant="extended"
          color="primary"
          aria-label="Find me in the log"
          onClick={handleOpen}
          sx={{
            position: 'fixed',
            right: { xs: 16, sm: 24 },
            bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
            zIndex: (activeTheme) => activeTheme.zIndex.speedDial
          }}>
          <GpsFixed sx={{ mr: 1 }} />
          Find me
        </Fab>
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="sm"
        aria-labelledby="find-me-title"
        slotProps={{ transition: { onExited: handleExited } }}>
        <DialogTitle
          id="find-me-title"
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <GpsFixed color="primary" />
            <Typography variant="h6" component="span">
              Find me
            </Typography>
          </Box>
          <IconButton onClick={handleClose} aria-label="Close find me" edge="end">
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers={!fullScreen} sx={{ p: 0, pb: 2 }}>
          <Box sx={{ px: 3, pt: 2 }}>
            <TextField
              autoFocus
              fullWidth
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              label="Name or BSA number"
              placeholder="e.g. Smith or 1234"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  )
                }
              }}
            />
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              {!hasQuery
                ? 'Only players in this event are searchable.'
                : total === 0
                  ? 'No matches'
                  : `${total} match${total === 1 ? '' : 'es'}`}
            </Typography>
          </Box>

          {hasQuery &&
            (results.length > 0 ? (
              <>
                <List disablePadding sx={{ mt: 1 }}>
                  {results.map((entry) => {
                    const chip = statusChip(entry);
                    const secondary = [entry.teamName, entry.sectionName, entry.venue]
                      .filter(Boolean)
                      .join(' · ');

                    return (
                      <ListItemButton
                        key={entry.key}
                        divider
                        onClick={() => handleSelect(entry)}>
                        <ListItemText
                          primary={entry.playerName}
                          secondary={secondary || 'Team not listed'}
                        />
                        {chip && (
                          <Chip
                            size="small"
                            label={chip.label}
                            color={chip.color}
                            sx={{ ml: 1 }}
                          />
                        )}
                      </ListItemButton>
                    );
                  })}
                </List>

                {truncated && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ px: 2, py: 1 }}>
                    Showing the first {results.length} of {total} matches. Keep typing to
                    narrow the search.
                  </Typography>
                )}
              </>
            ) : (
              <Box sx={{ px: 3, py: 2 }}>
                <Typography color="text.secondary">{noMatchMessage}</Typography>
              </Box>
            ))}
        </DialogContent>
      </Dialog>
    </>
  );
};

EmbeddedFindMe.propTypes = {
  entries: PropTypes.array.isRequired,
  located: PropTypes.object,
  onLocate: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired
};

export default EmbeddedFindMe;
